import { useEffect, useState } from 'react';
import type { LocalVideoTrack, RemoteVideoTrack } from 'livekit-client';

interface StatsSample {
  sentDisplay: string;
  recvDisplay: string;
  sentRes: string;
  recvRes: string;
  /** "address:port (candidateType)" of the SFU/relay endpoint actually delivering frames. */
  source: string;
}

const empty: StatsSample = { sentDisplay: '—', recvDisplay: '—', sentRes: '—', recvRes: '—', source: '—' };

interface CandStats { address?: string; ip?: string; port?: number; candidateType?: string; protocol?: string; relayProtocol?: string }
interface PairStats { state?: string; nominated?: boolean; selected?: boolean; localCandidateId?: string; remoteCandidateId?: string }

/**
 * Returns "<remote-ip>:<remote-port> via <local-summary>" where local-summary
 * tells you whether the path is direct (host/srflx) or going through TURN
 * (relay {protocol}). A "via relay ..." path means LiveKit Cloud's TURN is
 * relaying the media. The remote endpoint is always LiveKit's candidate
 * regardless of relay mode.
 */
function describePath(stats: RTCStatsReport): string {
  const cands = new Map<string, CandStats>();
  let active: PairStats | undefined;
  let fallback: PairStats | undefined;
  stats.forEach((r) => {
    if (r.type === 'remote-candidate' || r.type === 'local-candidate') {
      cands.set(r.id, r as RTCStats & CandStats);
    } else if (r.type === 'candidate-pair') {
      const p = r as RTCStats & PairStats;
      if ((p.nominated || p.selected) && p.state === 'succeeded' && !active) active = p;
      else if (p.state === 'succeeded' && !fallback) fallback = p;
    }
  });
  const pair = active ?? fallback;
  if (!pair) return '—';
  const remote = pair.remoteCandidateId ? cands.get(pair.remoteCandidateId) : undefined;
  const local  = pair.localCandidateId  ? cands.get(pair.localCandidateId)  : undefined;
  const remoteAddr = remote ? `${remote.address ?? remote.ip ?? '?'}:${remote.port ?? '?'}` : '?';
  const localType = local?.candidateType ?? '?';
  const localSummary = localType === 'relay'
    ? `relay ${local?.relayProtocol ?? local?.protocol ?? '?'}`
    : localType;
  return `${remoteAddr} via ${localSummary}`;
}

function fmtKbps(bps: number | null): string {
  if (bps == null) return '—';
  return bps >= 1_000_000 ? `${(bps / 1_000_000).toFixed(2)} Mbps` : `${Math.round(bps / 1000)} kbps`;
}

interface RtpSnapshot {
  sentBytes: number;
  recvBytes: number;
  framesEncoded: number;
  framesDecoded: number;
  ts: number;
}

// `sender` is marked @internal on LocalTrack but is a real public getter; cast through unknown.
type LocalTrackWithSender = LocalVideoTrack & { sender?: RTCRtpSender };

/**
 * Polls outbound-rtp stats from the publisher track's RTCRtpSender and
 * inbound-rtp stats from the subscribed track's RTCRtpReceiver. We bypass
 * `room.engine.pcManager` entirely (it's internal SDK state and the layout
 * isn't stable across versions). Track.sender / Track.receiver are the
 * documented surface and refer to exactly the right RTP transport.
 */
export function useStats(
  publisherTrack: LocalVideoTrack | null,
  mirrorTrack: RemoteVideoTrack | null,
): StatsSample {
  const [sample, setSample] = useState<StatsSample>(empty);

  useEffect(() => {
    if (!publisherTrack && !mirrorTrack) { setSample(empty); return; }
    let prev: RtpSnapshot | null = null;
    let cancelled = false;

    const tick = async () => {
      let sentBytes = 0, recvBytes = 0, framesEncoded = 0, framesDecoded = 0;
      let sentRes = '—', recvRes = '—', source = '—';

      const sender = (publisherTrack as LocalTrackWithSender | null)?.sender;
      if (sender) {
        const stats = await sender.getStats();
        // With simulcast there are multiple outbound-rtp video records (one per
        // layer). Sum bytesSent across all of them for the wire bandwidth, but
        // pick the largest-resolution layer for fps and resolution display —
        // its framesEncoded rate matches the source capture rate. (Summing
        // framesEncoded across layers double/triple-counts — e.g. 2 layers at
        // 32 fps would read as 64.)
        let topArea = -1;
        stats.forEach((r: RTCStats) => {
          if (r.type !== 'outbound-rtp') return;
          const o = r as RTCStats & {
            kind?: string; mediaType?: string;
            bytesSent?: number; framesEncoded?: number;
            frameWidth?: number; frameHeight?: number;
          };
          if (o.kind !== 'video' && o.mediaType !== 'video') return;
          sentBytes += o.bytesSent ?? 0;
          const area = (o.frameWidth ?? 0) * (o.frameHeight ?? 0);
          if (area > topArea) {
            topArea = area;
            framesEncoded = o.framesEncoded ?? 0;
            if (o.frameWidth && o.frameHeight) sentRes = `${o.frameWidth}x${o.frameHeight}`;
          }
        });
      }

      const receiver = mirrorTrack?.receiver;
      if (receiver) {
        const stats = await receiver.getStats();
        stats.forEach((r: RTCStats) => {
          if (r.type !== 'inbound-rtp') return;
          const i = r as RTCStats & {
            kind?: string; mediaType?: string;
            bytesReceived?: number; framesDecoded?: number;
            frameWidth?: number; frameHeight?: number;
          };
          if (i.kind !== 'video' && i.mediaType !== 'video') return;
          recvBytes += i.bytesReceived ?? 0;
          framesDecoded += i.framesDecoded ?? 0;
          if (i.frameWidth && i.frameHeight) recvRes = `${i.frameWidth}x${i.frameHeight}`;
        });
        source = describePath(stats);
      }

      const now = performance.now();
      let sentDisplay = '—', recvDisplay = '—';
      if (prev) {
        const dtSec = (now - prev.ts) / 1000;
        if (dtSec > 0) {
          const sentBps = ((sentBytes - prev.sentBytes) * 8) / dtSec;
          const recvBps = ((recvBytes - prev.recvBytes) * 8) / dtSec;
          const sentFps = (framesEncoded - prev.framesEncoded) / dtSec;
          const recvFps = (framesDecoded - prev.framesDecoded) / dtSec;
          sentDisplay = `${fmtKbps(sentBps)} @ ${sentFps.toFixed(0)} fps`;
          recvDisplay = `${fmtKbps(recvBps)} @ ${recvFps.toFixed(0)} fps`;
        }
      }
      prev = { sentBytes, recvBytes, framesEncoded, framesDecoded, ts: now };
      if (!cancelled) setSample({ sentDisplay, recvDisplay, sentRes, recvRes, source });
    };

    void tick();
    const id = setInterval(() => { void tick(); }, 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [publisherTrack, mirrorTrack]);

  return sample;
}
